import { useMemo } from "react";

import {
  sideCarouselSlides,
  type SideCarouselSlide,
} from "@unauthenticated/shared/components/SideCarouselPanel/sideCarouselSlides";
import { PromoSlide } from "@unauthenticated/shared/components/SideCarouselPanel/slides/PromoSlide/PromoSlide";
import { usePublicActivePatientPromotionSignUpBannerUnauthenticatedSignUpSignInQuery } from "@unauthenticated/signupSignin/data/queries/PublicActivePatientPromotionSignUpBannerQuery/PublicActivePatientPromotionSignUpBanner.query.generated";

interface Args {
  isCarouselVisible: boolean;
  isActivePromotion?: boolean;
  storeId?: string;
}

const useWelcomeCarouselSlides = ({
  isCarouselVisible,
  isActivePromotion,
  storeId,
}: Args): SideCarouselSlide[] => {
  const { data } = usePublicActivePatientPromotionSignUpBannerUnauthenticatedSignUpSignInQuery({
    // `storeId` is only read when the query runs, but the generated variables
    // type requires a string even alongside `skip`.
    variables: { storeId: storeId ?? "" },
    skip: !isCarouselVisible || !isActivePromotion || !storeId,
  });

  const promotion = data?.viewer?.publicActivePatientPromotions?.nodes?.[0];
  const backgroundColor = promotion?.patientBannerBackgroundColor;
  const headline = promotion?.patientSignUpBannerHeadline;
  const subtext = promotion?.patientSignUpBannerText;
  const bannerImageUrl = promotion?.patientSignUpBannerImageUrl;
  const bannerImage2xUrl = promotion?.patientSignUpBannerImage2xUrl;

  return useMemo(() => {
    if (!backgroundColor || !headline || !subtext || !bannerImageUrl || !bannerImage2xUrl) {
      return sideCarouselSlides;
    }

    const ActivePromoSlide = () => (
      <PromoSlide
        backgroundColor={backgroundColor}
        bannerImageUrl={bannerImageUrl}
        bannerImage2xUrl={bannerImage2xUrl}
        headline={headline}
        subtext={subtext}
      />
    );

    return [...sideCarouselSlides, { key: "promo", Slide: ActivePromoSlide }];
  }, [backgroundColor, headline, subtext, bannerImageUrl, bannerImage2xUrl]);
};

export { useWelcomeCarouselSlides };
